/**
 * PasswordStrengthMeter — inline-чек-лист требований к паролю + N-сегментная
 * прогресс-полоска (N = checks.length) и текстовый уровень справа от полоски.
 *
 * Уровни:
 *   passed ≤ 1                 → «Слишком слабый» (red)
 *   passed = 2                 → «Слабый» (amber)
 *   passed = 3                 → «Нормальный» (warm)
 *   passed = 4, длина < 14     → «Сильный» (cool)
 *   passed = 4, длина ≥ 14     → «Очень сильный» (green)
 *
 * Visibility: скрыт только когда поле пустое — иначе пользователь видит
 * прогресс и в позитивных уровнях («Сильный», «Очень сильный»).
 */
import { validatePassword } from '@/lib/validation'
import { Icon } from '@/components/icons/Icon'

interface Props {
  value: string
  /** Если true — показывает даже когда пусто (для showcase). */
  alwaysShow?: boolean
  className?: string
}

interface Level {
  label: string
  segments: number
  segmentClass: string
  textClass: string
}

function resolveLevel(passed: number, length: number, total: number): Level {
  if (passed <= 1) {
    return { label: 'Слишком слабый', segments: 1, segmentClass: 'bg-status-danger-text', textClass: 'text-status-danger-text' }
  }
  if (passed === 2) {
    return { label: 'Слабый', segments: 2, segmentClass: 'bg-status-warning-text', textClass: 'text-status-warning-text' }
  }
  if (passed === 3) {
    return { label: 'Нормальный', segments: 3, segmentClass: 'bg-accent-warm', textClass: 'text-accent-warm' }
  }
  // passed === total (все проверки)
  if (passed === total && length >= 14) {
    return { label: 'Очень сильный', segments: total, segmentClass: 'bg-status-success-text', textClass: 'text-status-success-text' }
  }
  return { label: 'Сильный', segments: total, segmentClass: 'bg-accent-cool', textClass: 'text-accent-cool' }
}

export function PasswordStrengthMeter({ value, alwaysShow = false, className }: Props) {
  const { checks } = validatePassword(value)
  const total = checks.length
  const passed = checks.filter((c) => c.passed).length
  const isEmpty = value.length === 0
  if (!alwaysShow && isEmpty) return null

  const lvl = resolveLevel(passed, value.length, total)

  return (
    <div className={`space-y-2 ${className ?? ''}`} aria-live="polite">
      <div className="flex items-center gap-3">
        <div
          className="flex gap-1 flex-1"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={total}
          aria-valuenow={lvl.segments}
        >
          {Array.from({ length: total }).map((_, i) => (
            <div
              key={i}
              className={`h-1.5 flex-1 rounded-full transition-all duration-300 ease-out ${
                i < lvl.segments ? lvl.segmentClass : 'bg-border-strong'
              }`}
            />
          ))}
        </div>
        <span className={`text-xs font-semibold whitespace-nowrap ${lvl.textClass}`}>
          {lvl.label}
        </span>
      </div>
      <ul className="space-y-1">
        {checks.map((c) => (
          <li
            key={c.label}
            className={`flex items-center gap-2 text-xs transition-colors ${
              c.passed ? 'text-text-muted line-through' : 'text-text-secondary'
            }`}
          >
            <Icon
              name={c.passed ? 'check-circle' : 'circle'}
              size={14}
              aria-hidden="true"
              className={`flex-shrink-0 ${c.passed ? 'text-status-success-text' : ''}`}
            />
            <span>{c.label}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
